<script setup lang="ts">
import { computed } from 'vue';
import type { Component } from 'vue';

type Tone = 'solid' | 'muted' | 'dashed';
type Padding = 'none' | 'sm' | 'md' | 'lg';

const props = withDefaults(
  defineProps<{
    as?: string | Component;
    tone?: Tone;
    padding?: Padding;
    unstyled?: boolean;
  }>(),
  {
    as: 'section',
    tone: 'solid',
    padding: 'md',
    unstyled: false
  }
);

const cardClasses = computed(() =>
  props.unstyled ? undefined : ['ui-card', `ui-card--${props.tone}`, `ui-card--padding-${props.padding}`]
);
</script>

<template>
  <component
    :is="as"
    v-bind="$attrs"
    :class="cardClasses"
  >
    <slot />
  </component>
</template>

<style scoped>
.ui-card {
  --ui-card-surface: var(--ui-card-bg, var(--ui-surface));
  --ui-card-border-color: var(--ui-card-border, var(--ui-border));
  --ui-card-shadow-value: var(--ui-card-shadow, var(--ui-shadow-panel));
  --ui-card-highlight: color-mix(in srgb, var(--ui-foreground-inverse) 32%, transparent);
  --ui-card-ambient: color-mix(in srgb, var(--ui-accent) 5%, transparent);

  position: relative;
  isolation: isolate;
  box-sizing: border-box;
  min-width: 0;
  max-width: 100%;
  border: 1px solid var(--ui-card-border-color);
  border-radius: var(--ui-panel-radius);
  background:
    radial-gradient(circle at 18% 0%, var(--ui-card-ambient), transparent 42%),
    linear-gradient(180deg, color-mix(in srgb, var(--ui-card-surface) 96%, var(--ui-foreground-inverse) 4%), var(--ui-card-surface));
  box-shadow: var(--ui-card-shadow-value);
  color: var(--ui-foreground);
  -webkit-backdrop-filter: blur(12px);
  backdrop-filter: blur(12px);
}

.ui-card::before {
  position: absolute;
  inset: 0;
  z-index: -1;
  border-radius: inherit;
  background: linear-gradient(180deg, var(--ui-card-highlight), transparent 38%);
  content: '';
  opacity: 0.42;
  pointer-events: none;
}

.ui-card--muted {
  --ui-card-border-color: color-mix(in srgb, var(--ui-card-border, var(--ui-border)) 88%, transparent);
  --ui-card-surface: var(--ui-card-bg-muted, var(--ui-surface-muted));
  --ui-card-ambient: color-mix(in srgb, var(--ui-accent) 4%, transparent);
}

.ui-card--dashed {
  --ui-card-border-color: var(--ui-border-strong);

  border-style: dashed;
  background:
    radial-gradient(circle at 18% 0%, color-mix(in srgb, var(--ui-accent) 6%, transparent), transparent 42%),
    var(--ui-card-surface);
}

.ui-card--padding-none {
  padding: 0;
}

.ui-card--padding-sm {
  padding: 1rem;
}

.ui-card--padding-md {
  padding: 1.5rem;
}

.ui-card--padding-lg {
  padding: 2rem;
}

@supports not ((-webkit-backdrop-filter: blur(8px)) or (backdrop-filter: blur(8px))) {
  .ui-card {
    background: var(--ui-card-surface);
  }
}

@media (max-width: 640px) {
  .ui-card--padding-md {
    padding: 1.125rem;
  }

  .ui-card--padding-lg {
    padding: 1.35rem;
  }
}
</style>
